import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import adminService from "../services/admin.service";
import pkClientService from "../services/pkClient.service";
import { Tab, Tabs, Table } from 'react-bootstrap';
import EventBus from "../common/EventBus";

const PKSubscriptionManager = () => {
    const { user: currentUser } = useSelector((state) => state.auth);

    const [searchTerm, setSearchTerm] = useState("");
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [clientSubscriptions, setClientSubscriptions] = useState([]);

    const [searchLoading, setSearchLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState('client-view');

    const [selectedSubIds, setSelectedSubIds] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editSub, setEditSub] = useState(null);
    const [editStart, setEditStart] = useState("");
    const [editEnd, setEditEnd] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
    const [selectedAvailableSubId, setSelectedAvailableSubId] = useState("");
    const [createStart, setCreateStart] = useState("");
    const [createEnd, setCreateEnd] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    const [subscriptionReference, setSubscriptionReference] = useState([]);
    const [selectedGlobalSubId, setSelectedGlobalSubId] = useState("");
    const [selectedSubDetails, setSelectedSubDetails] = useState(null);
    const [subClientsList, setSubClientsList] = useState([]);
    const [subClientsTotal, setSubClientsTotal] = useState(0);
    const [subClientsPage, setSubClientsPage] = useState(0);
    const subClientsPageSize = 50;
    const [subViewLoading, setSubViewLoading] = useState(false);
    const [subClientSearchTerm, setSubClientSearchTerm] = useState("");

    React.useEffect(() => {
        if (currentUser && currentUser.roles.includes("ROLE_ADMIN")) {
            pkClientService.getReferenceData().then(
                (res) => {
                    setSubscriptionReference(res.data.subscriptions || []);
                },
                (err) => {
                    console.error("Failed to load reference data for subscriptions", err);
                }
            );
        }
    }, [currentUser]);

    if (!currentUser) return <Navigate to="/login" />;
    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container">
                <header className="jumbotron">
                    <h3><strong>Unauthorized:</strong> You must be an administrator to access this page.</h3>
                </header>
            </div>
        );
    }

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            searchClients();
        }
    };

    const searchClients = () => {
        if (!searchTerm || searchTerm.trim().length === 0) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Search Error", message: "Please enter a client name to search." });
            return;
        }

        if (searchTerm.trim().length < 3) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Search Error", message: "Search term must be at least 3 characters long." });
            return;
        }

        setSearchLoading(true);
        adminService.getClients().then(
            (response) => {
                const filtered = response.data.filter(client =>
                    client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
                );

                setClients(filtered);
                setSearchLoading(false);

                if (filtered.length === 1) {
                    selectClient(filtered[0].oid, filtered[0].clientName);
                } else if (filtered.length === 0) {
                    toast.warning("No clients found matching that name.");
                } else {
                    setShowModal(true);
                }
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Error fetching clients." });
                setSearchLoading(false);
            }
        );
    };

    const selectClient = (clientId, clientName) => {
        setSelectedClientId(clientId);
        setSearchTerm(clientName);
        setShowModal(false);
        fetchClientDetailsAndSubscriptions(clientId);
    };

    const fetchClientDetailsAndSubscriptions = (clientId) => {
        setLoading(true);
        
        // Fetch client details
        pkClientService.getClientDetails(clientId).then(
            (res) => {
                setClientData(res.data);
            },
            (err) => {
                console.error("Failed to fetch client details", err);
            }
        );

        // Fetch subscriptions
        adminService.getClientSubscriptions(clientId).then(
            (res) => {
                setClientSubscriptions(res.data || []);
                setSelectedSubIds([]);
                setLoading(false);
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load PK Client subscriptions." });
                setLoading(false);
            }
        );
    };

    const formatDateForInput = (timestamp) => {
        if (!timestamp) return "";
        const d = new Date(timestamp);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${month}-${day}`;
    };

    const dateInputToTimestamp = (value) => {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day).getTime();
    };

    const todayForInput = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return formatDateForInput(d.getTime());
    };

    const handleSelectSub = (id) => {
        if (selectedSubIds.includes(id)) {
            setSelectedSubIds(selectedSubIds.filter(subId => subId !== id));
        } else {
            setSelectedSubIds([...selectedSubIds, id]);
        }
    };

    const handleSelectAll = (e) => {
        const currentList = activeTab === 'client-view' ? clientSubscriptions : subClientsList;
        if (e.target.checked) {
            const now = Date.now();
            const expiredSubs = currentList.filter(s => s.end_time && new Date(s.end_time).getTime() < now);
            setSelectedSubIds(expiredSubs.map(s => s.id));
        } else {
            setSelectedSubIds([]);
        }
    };

    const handleDeleteClick = () => {
        const now = Date.now();
        const currentList = activeTab === 'client-view' ? clientSubscriptions : subClientsList;
        const unexpiredSubs = currentList.filter(sub => 
            selectedSubIds.includes(sub.id) && sub.end_time && new Date(sub.end_time).getTime() > now
        );
        
        if (unexpiredSubs.length > 0) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { 
                title: "Validation Error", 
                message: "You have selected unexpired subscriptions. Only expired subscriptions can be deleted." 
            });
            return;
        }

        setShowDeleteConfirm(true);
    };

    const refreshCurrentData = () => {
        if (activeTab === 'client-view' && selectedClientId) {
            fetchClientDetailsAndSubscriptions(selectedClientId);
        } else if (activeTab === 'subscription-view' && selectedGlobalSubId) {
            fetchSubscriptionClients(selectedGlobalSubId, subClientsPage);
        }
    };

    const confirmDeleteSelected = () => {
        setActionLoading(true);
        adminService.deleteClientSubscriptions(selectedSubIds).then(
            () => {
                toast.success("Successfully deleted selected subscriptions.");
                setShowDeleteConfirm(false);
                setSelectedSubIds([]);
                setActionLoading(false);
                refreshCurrentData();
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Delete Error", message: err.response?.data || "Failed to delete." });
                setActionLoading(false);
            }
        );
    };

    const handleEditSub = (sub) => {
        setEditSub(sub);
        setEditStart(formatDateForInput(sub.start_time));
        setEditEnd(formatDateForInput(sub.end_time));
        setShowEditModal(true);
    };

    const saveEditSub = () => {
        if (!editStart || !editEnd) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Validation Error", message: "Start and End dates are required." });
            return;
        }

        const startTimestamp = new Date(editStart).getTime();
        const endTimestamp = new Date(editEnd).getTime();

        if (startTimestamp > endTimestamp) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Validation Error", message: "Start time cannot be after end time." });
            return;
        }

        setActionLoading(true);
        adminService.updateClientSubscriptionDates(editSub.id, { startTime: startTimestamp, endTime: endTimestamp }).then(
            () => {
                toast.success("Successfully updated subscription dates.");
                setShowEditModal(false);
                setActionLoading(false);
                refreshCurrentData();
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Update Error", message: err.response?.data || "Failed to update dates." });
                setActionLoading(false);
            }
        );
    };

    const openCreateSubscriptionModal = () => {
        if (!selectedClientId) return;
        setActionLoading(true);
        setSelectedAvailableSubId("");
        setCreateStart("");
        setCreateEnd("");
        adminService.getAvailableClientSubscriptions(selectedClientId).then(
            (res) => {
                setAvailableSubscriptions(res.data || []);
                setShowCreateModal(true);
                setActionLoading(false);
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: err.response?.data || "Failed to load available subscriptions." });
                setActionLoading(false);
            }
        );
    };

    const selectAvailableSubscription = (sub) => {
        setSelectedAvailableSubId(String(sub.id));
        setCreateStart(formatDateForInput(sub.defaultStartTime));
        setCreateEnd(formatDateForInput(sub.defaultEndTime));
    };

    const createSubscription = () => {
        if (!selectedAvailableSubId) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Validation Error", message: "Please select a subscription." });
            return;
        }
        if (!createStart || !createEnd) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Validation Error", message: "Start and End dates are required." });
            return;
        }
        if (createEnd < todayForInput()) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Validation Error", message: "End date cannot be before today." });
            return;
        }
        if (createStart > createEnd) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Validation Error", message: "Start date cannot be after end date." });
            return;
        }

        setActionLoading(true);
        adminService.createClientSubscription({
            clientId: selectedClientId,
            subscriptionId: Number(selectedAvailableSubId),
            startTime: dateInputToTimestamp(createStart),
            endTime: dateInputToTimestamp(createEnd)
        }).then(
            () => {
                toast.success("Successfully created subscription.");
                setShowCreateModal(false);
                setActionLoading(false);
                refreshCurrentData();
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Create Error", message: err.response?.data || "Failed to create subscription." });
                setActionLoading(false);
            }
        );
    };

    const fetchSubscriptionClients = (subId, page = 0, overrideSearch) => {
        if (!subId) return;
        setSubViewLoading(true);
        const searchQuery = typeof overrideSearch !== 'undefined' ? overrideSearch : subClientSearchTerm;
        adminService.getSubscriptionClients(subId, page, subClientsPageSize, searchQuery).then(
            (res) => {
                setSubClientsList(res.data.data || []);
                setSubClientsTotal(res.data.total || 0);
                setSelectedSubIds([]);
                setSubViewLoading(false);
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load clients for subscription." });
                setSubViewLoading(false);
            }
        );
    };

    const handleGlobalSubChange = (e) => {
        const subId = e.target.value;
        setSelectedGlobalSubId(subId);
        setSubClientsPage(0);
        setSelectedSubIds([]);
        setSubClientSearchTerm("");
        if (subId) {
            const details = subscriptionReference.find(s => String(s.id) === String(subId));
            setSelectedSubDetails(details || null);
            fetchSubscriptionClients(subId, 0, "");
        } else {
            setSelectedSubDetails(null);
            setSubClientsList([]);
            setSubClientsTotal(0);
        }
    };

    const handleSubClientsPageChange = (newPage) => {
        setSubClientsPage(newPage);
        fetchSubscriptionClients(selectedGlobalSubId, newPage);
    };


    // Removing getSubscriptionName as we inline the display now

    const truncateText = (text) => {
        if (!text) return '-';
        if (text.length > 15) {
            return <span title={text} style={{ cursor: 'help' }}>{text.substring(0, 12)}...</span>;
        }
        return <span title={text}>{text}</span>;
    };

    return (
        <div className="container-fluid py-4 px-4 px-lg-5 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h2 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>PK Subscription Management</h2>
            </div>

            <Tabs
                id="pk-subscription-tabs"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4 custom-tabs"
            >
                <Tab eventKey="client-view" title={<><i className="bi bi-person-lines-fill me-2"></i>Client View</>}>
                    {/* Search Box */}
                    <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                        <div className="card-body p-4">
                            <div className="row align-items-end">
                                <div className="col-md-10">
                                    <label htmlFor="clientSearch" className="form-label fw-bold">Search Client (Admin)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="clientSearch"
                                        placeholder="Enter client name (min 3 chars)..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onKeyPress={handleSearchKeyPress}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button
                                        className="btn-premium w-100 py-2 rounded"
                                        onClick={searchClients}
                                        disabled={searchLoading || searchTerm.trim().length < 3}
                                    >
                                        {searchLoading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-search me-2"></i>Search</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subscriptions Table */}
                    {selectedClientId && clientData && (
                        <div className="card border-0 shadow-lg mt-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="card-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0 fw-bold">
                                        <i className="bi bi-credit-card me-2"></i>
                                        Subscriptions for {clientData.clientName}
                                    </h4>
                                    <div>
                                        <button className="btn btn-sm btn-light me-3 fw-bold" onClick={openCreateSubscriptionModal} disabled={actionLoading}>
                                            <i className="bi bi-plus-circle me-1"></i> Add Subscription
                                        </button>
                                        {selectedSubIds.length > 0 && (
                                            <button className="btn btn-sm btn-danger me-3 fw-bold" onClick={handleDeleteClick}>
                                                <i className="bi bi-trash me-1"></i> Delete Selected ({selectedSubIds.length})
                                            </button>
                                        )}
                                        {loading && <span className="spinner-border spinner-border-sm text-light" role="status"></span>}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <div className="mt-2 text-muted">Loading subscriptions...</div>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table striped hover className="align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>
                                                        {(() => {
                                                            const expiredCount = clientSubscriptions.filter(s => s.end_time && new Date(s.end_time).getTime() < Date.now()).length;
                                                            return (
                                                                <input type="checkbox" className="form-check-input" 
                                                                    disabled={expiredCount === 0}
                                                                    checked={expiredCount > 0 && selectedSubIds.length === expiredCount}
                                                                    onChange={handleSelectAll} />
                                                            );
                                                        })()}
                                                    </th>
                                                    <th>ID</th>
                                                    <th>Subscription</th>
                                                    <th>Start Date</th>
                                                    <th>End Date</th>
                                                    <th>Payment ID</th>
                                                    <th>External Sub ID</th>
                                                    <th>Memo</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {clientSubscriptions.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center text-muted py-4">
                                                            No subscriptions found for this client.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    clientSubscriptions.map(sub => {
                                                        const isExpired = sub.end_time && new Date(sub.end_time).getTime() < Date.now();
                                                        return (
                                                        <tr key={sub.id} className={isExpired ? "table-danger" : ""}>
                                                            <td>
                                                                {isExpired && (
                                                                    <input type="checkbox" className="form-check-input" 
                                                                        checked={selectedSubIds.includes(sub.id)}
                                                                        onChange={() => handleSelectSub(sub.id)} />
                                                                )}
                                                            </td>
                                                            <td>{sub.id}</td>
                                                            <td>
                                                                <span className="me-2 fw-medium">{sub.subscriptionName || 'Unknown'}</span>
                                                                <span className="badge bg-secondary px-2 py-1">ID: {sub.subscription_id}</span>
                                                            </td>
                                                            <td>{sub.start_time ? new Date(sub.start_time).toLocaleDateString() : 'N/A'}</td>
                                                            <td>
                                                                {sub.end_time ? new Date(sub.end_time).toLocaleDateString() : 'N/A'}
                                                                {isExpired && <span className="badge bg-danger ms-2">Expired</span>}
                                                            </td>
                                                            <td>{truncateText(sub.payment_id)}</td>
                                                            <td>{truncateText(sub.external_subscription_id)}</td>
                                                            <td>{truncateText(sub.memo)}</td>
                                                            <td>
                                                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditSub(sub)}>
                                                                    <i className="bi bi-pencil"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Tab>

                <Tab eventKey="subscription-view" title={<><i className="bi bi-card-list me-2"></i>Subscription View</>}>
                    <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                        <div className="card-body p-4">
                            <label className="form-label fw-bold">Select Subscription</label>
                            <select className="form-select" value={selectedGlobalSubId} onChange={handleGlobalSubChange}>
                                <option value="">-- Select Subscription --</option>
                                {subscriptionReference.map(sub => (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.subscriptionName} ({sub.currency || 'USD'})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedSubDetails && (
                        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', borderLeft: '5px solid var(--primary-gradient)' }}>
                            <div className="card-body p-4 bg-light">
                                <h5 className="fw-bold mb-3">Subscription Details</h5>
                                <div className="row g-3">
                                    <div className="col-md-3"><strong>ID:</strong> {selectedSubDetails.id}</div>
                                    <div className="col-md-3"><strong>Name:</strong> {selectedSubDetails.subscriptionName}</div>
                                    <div className="col-md-3"><strong>Max Users:</strong> {selectedSubDetails.maxUsers}</div>
                                    <div className="col-md-3"><strong>Active:</strong> {selectedSubDetails.active ? 'Yes' : 'No'}</div>
                                    <div className="col-md-3"><strong>Price:</strong> {selectedSubDetails.price} {selectedSubDetails.currency}</div>
                                    <div className="col-md-3"><strong>Created:</strong> {selectedSubDetails.createdAt ? new Date(selectedSubDetails.createdAt).toLocaleDateString() : 'N/A'}</div>
                                    <div className="col-md-3"><strong>Updated:</strong> {selectedSubDetails.updateAt ? new Date(selectedSubDetails.updateAt).toLocaleDateString() : 'N/A'}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedGlobalSubId && (
                        <div className="card border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="card-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0 fw-bold">
                                        <i className="bi bi-people me-2"></i>
                                        Clients with this Subscription
                                    </h4>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex gap-2 me-3" style={{ width: '260px' }}>
                                            <input 
                                                type="text" 
                                                className="form-control form-control-sm" 
                                                placeholder="Search client name..." 
                                                value={subClientSearchTerm}
                                                onChange={(e) => setSubClientSearchTerm(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        setSubClientsPage(0);
                                                        fetchSubscriptionClients(selectedGlobalSubId, 0, subClientSearchTerm);
                                                    }
                                                }}
                                            />
                                            <button className="btn btn-sm btn-light" onClick={() => {
                                                setSubClientsPage(0);
                                                fetchSubscriptionClients(selectedGlobalSubId, 0, subClientSearchTerm);
                                            }}>
                                                <i className="bi bi-search"></i>
                                            </button>
                                        </div>
                                        {selectedSubIds.length > 0 && (
                                            <button className="btn btn-sm btn-danger me-3 fw-bold" onClick={handleDeleteClick}>
                                                <i className="bi bi-trash me-1"></i> Delete Selected ({selectedSubIds.length})
                                            </button>
                                        )}
                                        {subViewLoading && <span className="spinner-border spinner-border-sm text-light" role="status"></span>}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                {subViewLoading && subClientsList.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <Table striped hover className="align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>
                                                            {(() => {
                                                                const expiredCount = subClientsList.filter(s => s.end_time && new Date(s.end_time).getTime() < Date.now()).length;
                                                                return (
                                                                    <input type="checkbox" className="form-check-input" 
                                                                        disabled={expiredCount === 0}
                                                                        checked={expiredCount > 0 && selectedSubIds.length === expiredCount}
                                                                        onChange={handleSelectAll} />
                                                                );
                                                            })()}
                                                        </th>
                                                        <th>Client Name</th>
                                                        <th>Email</th>
                                                        <th>Phone</th>
                                                        <th>Start Date</th>
                                                        <th>End Date</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {subClientsList.length === 0 ? (
                                                        <tr><td colSpan="7" className="text-center text-muted py-4">No clients found for this subscription.</td></tr>
                                                    ) : (
                                                        subClientsList.map(sub => {
                                                            const isExpired = sub.end_time && new Date(sub.end_time).getTime() < Date.now();
                                                            return (
                                                            <tr key={sub.id} className={isExpired ? "table-danger" : ""}>
                                                                <td>
                                                                    {isExpired && (
                                                                        <input type="checkbox" className="form-check-input" 
                                                                            checked={selectedSubIds.includes(sub.id)}
                                                                            onChange={() => handleSelectSub(sub.id)} />
                                                                    )}
                                                                </td>
                                                                <td className="fw-bold text-primary">{sub.client_name}</td>
                                                                <td>{sub.email}</td>
                                                                <td>{sub.phone}</td>
                                                                <td>{sub.start_time ? new Date(sub.start_time).toLocaleDateString() : 'N/A'}</td>
                                                                <td>
                                                                    {sub.end_time ? new Date(sub.end_time).toLocaleDateString() : 'N/A'}
                                                                    {isExpired && <span className="badge bg-danger ms-2">Expired</span>}
                                                                </td>
                                                                <td>
                                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditSub(sub)}>
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <span className="text-muted">Total records: {subClientsTotal}</span>
                                            <div>
                                                <button className="btn btn-sm btn-outline-secondary me-2" disabled={subClientsPage === 0 || subViewLoading} onClick={() => handleSubClientsPageChange(subClientsPage - 1)}>Previous</button>
                                                <span className="me-2">Page {subClientsPage + 1}</span>
                                                <button className="btn btn-sm btn-outline-secondary" disabled={(subClientsPage + 1) * subClientsPageSize >= subClientsTotal || subViewLoading} onClick={() => handleSubClientsPageChange(subClientsPage + 1)}>Next</button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </Tab>
            </Tabs>

            {/* Client Selection Modal */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                                <h5 className="modal-title fw-bold"><i className="bi bi-search me-2"></i>Select Client</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-4 bg-light">
                                <div className="list-group">
                                    {clients.map(client => (
                                        <button
                                            key={client.oid}
                                            className="list-group-item list-group-item-action border-0 mb-2 shadow-sm rounded-3"
                                            onClick={() => selectClient(client.oid, client.clientName)}
                                        >
                                            <div className="d-flex w-100 justify-content-between align-items-center">
                                                <h6 className="mb-1 fw-bold text-primary">{client.clientName}</h6>
                                                <i className="bi bi-chevron-right text-muted"></i>
                                            </div>
                                            <small className="text-muted">{client.email} | {client.phone}</small>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Subscription Modal */}
            {showCreateModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                                <h5 className="modal-title fw-bold"><i className="bi bi-plus-circle me-2"></i>Add Subscription</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => !actionLoading && setShowCreateModal(false)}></button>
                            </div>
                            <div className="modal-body p-4 bg-light">
                                {availableSubscriptions.length === 0 ? (
                                    <div className="text-center text-muted py-4">No active subscriptions match this client's country.</div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '48px' }}></th>
                                                    <th>Subscription</th>
                                                    <th>Price</th>
                                                    <th>Currency</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {availableSubscriptions.map(sub => (
                                                    <tr key={sub.id} onClick={() => selectAvailableSubscription(sub)} style={{ cursor: 'pointer' }}>
                                                        <td>
                                                            <input
                                                                type="radio"
                                                                className="form-check-input"
                                                                name="availableSubscription"
                                                                checked={selectedAvailableSubId === String(sub.id)}
                                                                onChange={() => selectAvailableSubscription(sub)}
                                                            />
                                                        </td>
                                                        <td className="fw-medium">{sub.subscriptionName}</td>
                                                        <td>{sub.price}</td>
                                                        <td><span className="badge bg-secondary">{sub.currency}</span></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                                <div className="row mt-4">
                                    <div className="col-md-6 mb-3 mb-md-0">
                                        <label className="form-label fw-bold">Start Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={createStart}
                                            onChange={(e) => setCreateStart(e.target.value)}
                                            disabled={actionLoading || !selectedAvailableSubId}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">End Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={createEnd}
                                            min={todayForInput()}
                                            onChange={(e) => setCreateEnd(e.target.value)}
                                            disabled={actionLoading || !selectedAvailableSubId}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-top-0 bg-light">
                                <button type="button" className="btn btn-secondary px-4 rounded-pill" onClick={() => setShowCreateModal(false)} disabled={actionLoading}>Cancel</button>
                                <button type="button" className="btn btn-primary px-4 rounded-pill fw-bold" onClick={createSubscription} disabled={actionLoading || !selectedAvailableSubId || !createStart || !createEnd}>
                                    {actionLoading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-plus-circle me-2"></i>}
                                    Create
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header border-bottom-0 py-3 bg-danger text-white">
                                <h5 className="modal-title fw-bold"><i className="bi bi-exclamation-triangle me-2"></i>Confirm Deletion</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => !actionLoading && setShowDeleteConfirm(false)}></button>
                            </div>
                            <div className="modal-body p-4 bg-light text-center">
                                <p className="mb-0 fs-5">Are you sure you want to delete {selectedSubIds.length} subscription(s)?</p>
                                <p className="text-muted mt-2">Only expired subscriptions can be deleted. This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer border-top-0 bg-light justify-content-center">
                                <button type="button" className="btn btn-secondary px-4 rounded-pill" onClick={() => setShowDeleteConfirm(false)} disabled={actionLoading}>Cancel</button>
                                <button type="button" className="btn btn-danger px-4 rounded-pill fw-bold" onClick={confirmDeleteSelected} disabled={actionLoading}>
                                    {actionLoading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-trash me-2"></i>}
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Dates Modal */}
            {showEditModal && editSub && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                                <h5 className="modal-title fw-bold"><i className="bi bi-pencil-square me-2"></i>Edit Subscription Dates</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => !actionLoading && setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body p-4 bg-light">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Start Date</label>
                                    <input type="date" className="form-control" value={editStart} onChange={(e) => setEditStart(e.target.value)} disabled={actionLoading} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">End Date</label>
                                    <input type="date" className="form-control" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} disabled={actionLoading} />
                                </div>
                            </div>
                            <div className="modal-footer border-top-0 bg-light">
                                <button type="button" className="btn btn-secondary px-4 rounded-pill" onClick={() => setShowEditModal(false)} disabled={actionLoading}>Cancel</button>
                                <button type="button" className="btn btn-primary px-4 rounded-pill fw-bold" onClick={saveEditSub} disabled={actionLoading}>
                                    {actionLoading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-save me-2"></i>}
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PKSubscriptionManager;
